import React, { useEffect, useRef, useState } from "react";
import { RCCHookToast, RCCIcon, RCCRefSpreadsheetProps, RCCServicesApi } from "react-common-components";
import Loader from "src/components/Loader";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import Spreadsheet from "src/components/Spreadsheet";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import ReactDOMServer from "react-dom/server";
import { simplePermissionType } from "src/components/Permissions/interface";
import { IMatbc001 } from "../interface";
import ModalAttachmentsMatrizBcg, { IMatrizBcgAttachmentsRef } from "./modalAttachment";
import ModalCommentsMatrizBcg, { IMatrizBcgCommentsRef } from "./modalComments";

export interface ISpreadSheetMatrizBcg{
    selectedMatrizBcg: IMatbc001;
}

const SpreadSheetMatrizBcg: React.FC<ISpreadSheetMatrizBcg> = ({
	selectedMatrizBcg
}) => {

	const { translate, currentLanguage} = useTranslation();
	const {checkIfHasSpecificPermission, administrator, superUser} = useAuth();
	const { addToast } = RCCHookToast();

	const [ data, setData] = useState<string[][] | null>(null);
	const [loading, setIsLoading] = useState(false);

	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const modalAttachment = useRef<IMatrizBcgAttachmentsRef>(null);
	const modalComments = useRef<IMatrizBcgCommentsRef>(null);

	const loadData = async () => {
		try{
			setIsLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "AnalysisMatrizBcg/SelectData",
				urlParameters: {
					id: selectedMatrizBcg.mac001Id
				}
			});

			const dataLength = response.data.length - 1;
			const dt = response.data.map((row: any, i: number) => {
				const newRow = row;
				if(newRow[1] !== null && newRow[1] !== ""){
					newRow[8] = i === dataLength ? "" : ReactDOMServer.renderToStaticMarkup(<div className="data-action-jexcel-comment-analysis-matrizbcg" data-description={newRow[0]} data-id={newRow[1]}><RCCIcon name={ newRow[8] === "0" ?  "comment" : "commentFilled"} color="#aaa" size={16} /></div>);
					newRow[9] = i === dataLength ? "" : ReactDOMServer.renderToStaticMarkup(<div className="data-action-jexcel-attachment-analysis-matrizbcg" data-description={newRow[0]} data-id={newRow[1]}><RCCIcon name="attachment" color="#aaa" size={16}/> {newRow[9] === "0" ? "" : newRow[9]}</div>);
				}
				return newRow;
			});

			setData(dt);
			setIsLoading(false);	
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setIsLoading(false);
		}
	};

	const onSaveSpreadsheet = async () => {
		try{
			const newValuesData: string[][] = spreadsheetRef.current ? spreadsheetRef.current.getData() as string[][] : [];
			newValuesData.pop();
			const newValueAnalysisData = newValuesData.map((line) => line.map((item) => String(item)));
			
			await RCCServicesApi.putRequest({
				method: "AnalysisMatrizBcg/UpdateData",
				body: {
					currentLanguage,
					spreadsheet: {
						data: newValueAnalysisData
					}
				}
			});

			addToast({
				color: "green",
				description: translate("Valores adicionados com sucesso"),
				title: translate("Sucesso")
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar valores"), addToast);
		}
	};

	useEffect(() => {
		loadData();
	},[selectedMatrizBcg]);

	return (
		<>
			{
				!loading && data &&
                <Spreadsheet 
                	ref={spreadsheetRef}
                	data={data}
                	editable={(administrator || superUser || selectedMatrizBcg.permission >= Number(simplePermissionType.VIEW_AND_EDIT)) && checkIfHasSpecificPermission("analysis/matrizbcg", PermissionEnum.CREATE_UPDATE)}
                	columns={[
                		{ type: "text", title: translate("Itens BCG"), width: 200, readOnly: true, wordWrap: true },
                		{ type: "hidden", title: "", width: 0 },
                		{ type: "numeric", title: `${translate("Crescimento de Mercado")} (%)`, width: 120,  decimal: "," },
                		{ type: "numeric", title: `Market Share ${translate("Empresa")} (%)`, width: 120, decimal: "," },
                		{ type: "numeric", title: `Market Share ${translate("Rival")} (%)`, width: 120,  decimal: "," },
                		{ type: "numeric", title: `Market Share ${translate("Relativo")} (%)`, width: 120, readOnly: true, decimal: "," },
                		{ type: "numeric", title: `${translate("Faturamento")} (MR$)`, width: 120, decimal: "," },
                		{ type: "numeric", title: `${translate("Faturamento")} (%)`, width: 120, readOnly: true,  decimal: "," },
                		{ type: "html", title: " ", width: 50, readOnly: true },
                		{ type: "html", title: " ", width: 50, readOnly: true }
                	]}
                	readonlyLastRows={1}
                	format={{
                		columns: [1,2,3,4,5,6,7],
                		customColumnsOnReadonlyLastRows: [6,7]
                	}}
                	showButtons={(administrator || superUser || selectedMatrizBcg.permission >= Number(simplePermissionType.VIEW_AND_EDIT)) && checkIfHasSpecificPermission("analysis/matrizbcg", PermissionEnum.CREATE_UPDATE)}
                	actionSaveButton={onSaveSpreadsheet}
                	onMount={() => {
                		if(spreadsheetRef && spreadsheetRef.current){
                			const refComments = spreadsheetRef.current?.HTMLElement?.querySelectorAll("div.data-action-jexcel-comment-analysis-matrizbcg");
                			const refAttachments = spreadsheetRef.current?.HTMLElement?.querySelectorAll("div.data-action-jexcel-attachment-analysis-matrizbcg");
                			refComments?.forEach((ab: any) => {
                				const idItemBcg = ab.getAttribute("data-id"); 
                				const descriptionItemBcg = ab.getAttribute("data-description");
                				ab.addEventListener("click", () => {
                					modalComments.current?.openModalComments(idItemBcg, descriptionItemBcg,(administrator || superUser || selectedMatrizBcg.permission >= 2) && checkIfHasSpecificPermission("analysis/marketshare", PermissionEnum.CREATE_UPDATE), loadData);
                				});
                			});
                			refAttachments?.forEach((ab: any) => {
                				const idItemBcg = ab.getAttribute("data-id"); 
                				const descriptionItemBcg = ab.getAttribute("data-description");
                				ab.addEventListener("click", () => {
                					modalAttachment.current?.openModalAttachments(idItemBcg, descriptionItemBcg, (administrator || superUser || selectedMatrizBcg.permission >= 2) && checkIfHasSpecificPermission("analysis/marketshare", PermissionEnum.CREATE_UPDATE), loadData);
                				});
                			});
                		}
                	}}
                />
			}

			{
				loading &&
				<Loader/>
			}

			<ModalAttachmentsMatrizBcg 
				ref={modalAttachment}
			/>

			<ModalCommentsMatrizBcg
				ref={modalComments}
			/>

		</>
	);
};


export default SpreadSheetMatrizBcg;
